'use client';

import { useEffect, useRef } from 'react';

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.12 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal-hidden, .reveal-left, .reveal-right');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const highlights = [
    { icon: '🏛️', title: 'US Registered LLC', desc: 'Incorporated in Wyoming, USA — legally compliant and internationally recognized.' },
    { icon: '🌐', title: 'International Reach', desc: 'We serve clients across North America, Europe, the Middle East, and Asia-Pacific.' },
    { icon: '📋', title: 'Transparent Process', desc: 'Structured 50/50 payment model with clear milestones and documented deliverables.' },
    { icon: '🔐', title: 'Secure Transactions', desc: 'All payments processed via Stripe and PayPal — SSL-secured with zero stored card data.' },
  ];

  return (
    <section id="about" ref={sectionRef} className="py-24 lg:py-32" style={{ background: '#fff' }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div className="reveal-left">
            <span className="section-label">About Cartelix LLC</span>
            <div className="section-divider" />
            <h2
              className="font-heading font-extrabold leading-tight mb-6"
              style={{ fontSize: 'clamp(2rem, 3.5vw, 2.8rem)', color: '#0D1A3A', letterSpacing: '-0.025em' }}
            >
              A US Digital Agency<br />
              You Can Trust.
            </h2>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#374151', maxWidth: 500 }}>
              Cartelix LLC is a Wyoming-registered digital agency with over 8 years of hands-on
              experience building digital products for businesses across six continents. We combine
              strategic thinking with technical execution to deliver results that matter.
            </p>
            <p className="text-base leading-relaxed mb-8" style={{ color: '#374151', maxWidth: 500 }}>
              Whether you&apos;re a startup launching your first product or an established company
              seeking a digital transformation, our team brings the expertise, accountability, and
              international perspective to move your business forward.
            </p>

            {/* Credentials strip */}
            <div
              className="flex flex-wrap gap-3 p-5 rounded-2xl"
              style={{ background: '#EEF2FF', border: '1px solid rgba(31,58,138,0.1)' }}
            >
              <div style={{ fontSize: '13px', color: '#1F3A8A', fontWeight: 600, fontFamily: '"DM Sans",sans-serif' }}>
                📍 30 N Gould St Ste R, Sheridan, WY 82801, USA
              </div>
              <div style={{ fontSize: '13px', color: '#1F3A8A', fontWeight: 600, fontFamily: '"DM Sans",sans-serif' }}>
                ✉️ contact@cartelix-llc.com
              </div>
              <div style={{ fontSize: '13px', color: '#1F3A8A', fontWeight: 600, fontFamily: '"DM Sans",sans-serif' }}>
                📞 +1 (334) 583-1026
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <a href="#contact" className="btn-primary">Work With Us</a>
              <a href="#services" className="btn-outline">Our Services</a>
            </div>
          </div>

          {/* Right */}
          <div className="reveal-right">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {highlights.map((item) => (
                <div
                  key={item.title}
                  className="p-6 rounded-2xl transition-all duration-300"
                  style={{ background: '#F8FAFF', border: '1px solid #E5E9F5' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 32px rgba(31,58,138,0.1)';
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(31,58,138,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                    (e.currentTarget as HTMLDivElement).style.borderColor = '#E5E9F5';
                  }}
                >
                  <div className="text-2xl mb-3">{item.icon}</div>
                  <h4
                    className="font-heading font-bold mb-2"
                    style={{ fontSize: '15px', color: '#0D1A3A' }}
                  >
                    {item.title}
                  </h4>
                  <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.6 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}